import React, { useMemo, useState, useEffect } from "react";
import { isNative, loadReminderChoice, setReminder } from "../utils/reminder";

const PRESETS = [
  { key: "07:00", labelFr: "07h", labelEn: "7:00 AM", hour: 7, minute: 0 },
  { key: "10:00", labelFr: "10h", labelEn: "10:00 AM", hour: 10, minute: 0 },
  { key: "13:00", labelFr: "13h", labelEn: "1:00 PM", hour: 13, minute: 0 },
  { key: "17:00", labelFr: "17h", labelEn: "5:00 PM", hour: 17, minute: 0 },
  { key: "19:00", labelFr: "19h", labelEn: "7:00 PM", hour: 19, minute: 0 },
  { key: "21:00", labelFr: "21h", labelEn: "9:00 PM", hour: 21, minute: 0 },
  { key: "23:00", labelFr: "23h", labelEn: "11:00 PM", hour: 23, minute: 0 },
];


export default function ReminderSettings({ language, onBack }) {
  const [choice, setChoice] = useState("off"); // "off" ou "HH:MM"
  const [status, setStatus] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      const saved = await loadReminderChoice();
      if (!mounted) return;

      if (!saved || saved.enabled === false) {
        setChoice("off");
        return;
      }

      // Reconstitue la clé "HH:MM" depuis hour/minute
      const hh = String(saved.hour).padStart(2, "0");
      const mm = String(saved.minute).padStart(2, "0");
      const key = `${hh}:${mm}`;

      // Si la valeur sauvegardée ne correspond pas à un preset, on met off
      const exists = PRESETS.some((p) => p.key === key);
      setChoice(exists ? key : "off");
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const labels = useMemo(() => {
    return {
      title: language === "fr" ? "Rappel quotidien" : "Daily reminder",
      subtitle:
        language === "fr"
          ? "Choisis une heure (optionnel). Tu peux désactiver à tout moment."
          : "Pick a time (optional). You can turn it off anytime.",
      off: language === "fr" ? "Désactivé" : "Off",
      save: language === "fr" ? "Enregistrer" : "Save",
      back: language === "fr" ? "Retour" : "Back",
      webNote:
        language === "fr"
          ? "⚠️ Les rappels fonctionnent uniquement dans l’app Android/iOS (pas dans le navigateur)."
          : "⚠️ Reminders work only in the Android/iOS app (not in the browser).",
      ok: language === "fr" ? "✅ Rappel mis à jour." : "✅ Reminder updated.",
      err:
        language === "fr"
          ? "❌ Impossible d’activer le rappel (permission refusée ?)."
          : "❌ Couldn't enable reminder (permission denied?).",
    };
  }, [language]);

  const applyChoice = async () => {
    setStatus("");
    try {
      if (choice === "off") {
        await setReminder({ enabled: false, language });
      } else {
        const preset = PRESETS.find((p) => p.key === choice);
        if (!preset) return;

        await setReminder({
          enabled: true,
          hour: preset.hour,
          minute: preset.minute,
          language,
        });
      }
      setStatus(labels.ok);
    } catch (e) {
      setStatus(labels.err);
    }
  };

  return (
    <main className="night-screen">
      <div className="portrait-panel">
        <div className="panel-header-card">
          <h1 className="panel-title">{labels.title}</h1>
          <p className="panel-subtitle secondary">{labels.subtitle}</p>

          {!isNative() ? (
            <p className="panel-subtitle secondary" style={{ marginTop: "0.6rem" }}>
              {labels.webNote}
            </p>
          ) : null}

          <div style={{ marginTop: "0.8rem", display: "grid", gap: "10px" }}>
            <label className="sleep-mini-check" style={{ justifyContent: "flex-start" }}>
              <input
                type="radio"
                checked={choice === "off"}
                onChange={() => setChoice("off")}
              />
              <span>{labels.off}</span>
            </label>

            {PRESETS.map((p) => (
              <label
                key={p.key}
                className="sleep-mini-check"
                style={{ justifyContent: "flex-start" }}
              >
                <input
                  type="radio"
                  checked={choice === p.key}
                  onChange={() => setChoice(p.key)}
                />
                <span>{language === "fr" ? p.labelFr : p.labelEn}</span>
              </label>
            ))}
          </div>

          <button
            type="button"
            className="primary-button"
            style={{ marginTop: "1rem" }}
            onClick={applyChoice}
          >
            {labels.save}
          </button>

          {status ? (
            <p className="panel-subtitle secondary" style={{ marginTop: "0.6rem" }}>
              {status}
            </p>
          ) : null}
        </div>

        <button className="secondary-button back-button" onClick={onBack}>
          {labels.back}
        </button>
      </div>
    </main>
  );
}
